import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import GenericCrudPage from "./GenericCrudPage";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Package, CheckCircle2, AlertTriangle, Wrench, ArrowDown, ArrowUp, Wallet, Plus, Boxes, Building, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CONDITIONS = ["Good","Fair","Damaged","Under Maintenance"];
const OF_CATS = ["Peralatan","Elektronik","Furnitur","IT","Konsumable","Lainnya"];

const condBadge = (c) => {
  const m = { Good:"bg-emerald-100 text-emerald-700", Fair:"bg-amber-100 text-amber-700", Damaged:"bg-red-100 text-red-700", "Under Maintenance":"bg-sky-100 text-sky-700" };
  return <Badge className={m[c] || "bg-slate-100 text-slate-700"}>{c}</Badge>;
};
const stockStatus = (r) => {
  const a = Number(r.available||0), m = Number(r.minimum||0);
  if (a <= 0) return <Badge className="bg-red-100 text-red-700">OUT OF STOCK</Badge>;
  if (a <= m) return <Badge className="bg-amber-100 text-amber-700">LOW STOCK</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700">AVAILABLE</Badge>;
};

const inventoryFields = () => ([
  { key: "item_code", label: "Kode", mono: true },
  { key: "item_name", label: "Nama Item", colSpan: 2 },
  { key: "category", label: "Kategori", type: "select", options: OF_CATS },
  { key: "quantity", label: "Qty", type: "number" },
  { key: "available", label: "Tersedia", type: "number" },
  { key: "in_use", label: "Dipakai", type: "number" },
  { key: "damaged", label: "Rusak", type: "number" },
  { key: "minimum", label: "Min", type: "number" },
  { key: "location", label: "Lokasi" },
  { key: "condition", label: "Kondisi", type: "select", options: CONDITIONS, badge: condBadge },
  { key: "pic", label: "PIC" },
  { key: "status_stock", label: "Status", hideInForm: true, format: (_, r) => stockStatus(r) },
  { key: "notes", label: "Catatan", type: "textarea", colSpan: 2, hideInTable: true },
]);

const inventorySummary = [
  { label:"Total Item", icon:Boxes, tone:"bg-slate-100 text-slate-700", compute:(d)=>d.length },
  { label:"Total Qty", icon:Package, tone:"bg-sky-100 text-sky-700", compute:(d)=>d.reduce((s,x)=>s+Number(x.quantity||0),0) },
  { label:"Tersedia", icon:CheckCircle2, tone:"bg-emerald-100 text-emerald-700", compute:(d)=>d.reduce((s,x)=>s+Number(x.available||0),0) },
  { label:"Rusak/Perbaikan", icon:Wrench, tone:"bg-red-100 text-red-700", compute:(d)=>d.reduce((s,x)=>s+Number(x.damaged||0),0) },
];

const defaultInv = { item_code:"", item_name:"", category: OF_CATS[0], quantity:0, available:0, in_use:0, damaged:0, minimum:0, location:"", condition:"Good", pic:"", notes:"" };

export function InventarisOFPage() {
  return <GenericCrudPage resource="jktof/inventaris-of" title="Inventaris OF" subtitle="JKTOF · Operational Flight"
    moduleKey="jktof" testidPrefix="jktof-of" fields={inventoryFields()} defaultItem={defaultInv}
    searchKeys={["item_code","item_name","location","pic"]}
    filterFields={[{key:"category",label:"Kategori",options:OF_CATS},{key:"condition",label:"Kondisi",options:CONDITIONS}]}
    summaryCards={inventorySummary}/>;
}
export function InventarisOFCPage() {
  return <GenericCrudPage resource="jktof/inventaris-ofc" title="Inventaris OFC" subtitle="JKTOF · Operational Flight Control"
    moduleKey="jktof" testidPrefix="jktof-ofc" fields={inventoryFields()} defaultItem={defaultInv}
    searchKeys={["item_code","item_name","location","pic"]}
    filterFields={[{key:"category",label:"Kategori",options:OF_CATS},{key:"condition",label:"Kondisi",options:CONDITIONS}]}
    summaryCards={inventorySummary}/>;
}

// ---------- Cash Management ----------
const CASH_CATS = ["Iuran Kas","Konsumsi","ATK","Perbaikan","Transport","Kegiatan","Refund","Donasi","Lainnya"];
const rupiah = (n) => "Rp " + Number(n||0).toLocaleString("id-ID");

export function UangKasPage() {
  const qc = useQueryClient();
  const { canWrite } = useAuth();
  const canEdit = canWrite("jktof_cash");
  const [q, setQ] = useState("");
  const [ty, setTy] = useState("ALL");
  const [cat, setCat] = useState("ALL");
  const [month, setMonth] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { data = [] } = useQuery({ queryKey:["jktof-cash"], queryFn: async () => (await apiClient.get("/jktof/cash")).data });

  const months = useMemo(()=>Array.from(new Set(data.map(d=>(d.date||"").slice(0,7)))).sort(), [data]);

  const filtered = useMemo(()=>data
    .filter(d => (!q || (d.description||"").toLowerCase().includes(q.toLowerCase()) || (d.transaction_number||"").toLowerCase().includes(q.toLowerCase()))
      && (ty==="ALL"||d.type===ty) && (cat==="ALL"||d.category===cat) && (month==="ALL"||(d.date||"").startsWith(month))
    ).sort((a,b)=>(a.date||"").localeCompare(b.date||"")),
    [data,q,ty,cat,month]);

  // Running balance
  const withBalance = useMemo(()=>{
    let bal = 0;
    return filtered.map(t => { bal += (t.type==="Pemasukan"?1:-1) * Number(t.amount||0); return {...t, running: bal}; });
  }, [filtered]);

  const totalIn = data.filter(d=>d.type==="Pemasukan").reduce((s,x)=>s+Number(x.amount||0),0);
  const totalOut = data.filter(d=>d.type==="Pengeluaran").reduce((s,x)=>s+Number(x.amount||0),0);
  const balance = totalIn - totalOut;

  const monthly = useMemo(()=>{
    const m = {};
    data.forEach(d=>{const k=(d.date||"").slice(0,7); if(!k)return; m[k]=m[k]||{month:k,income:0,expense:0}; if(d.type==="Pemasukan") m[k].income+=Number(d.amount||0); else m[k].expense+=Number(d.amount||0);});
    return Object.values(m).sort((a,b)=>a.month.localeCompare(b.month));
  }, [data]);

  const mSave = useMutation({
    mutationFn: async (b) => b.id ? (await apiClient.put(`/jktof/cash/${b.id}`, b)).data : (await apiClient.post("/jktof/cash", b)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["jktof-cash"]}); qc.invalidateQueries({queryKey:["jktof-dashboard"]}); toast.success("Transaksi disimpan"); setOpen(false); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menyimpan"),
  });

  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/jktof/cash/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({queryKey:["jktof-cash"]}); qc.invalidateQueries({queryKey:["jktof-dashboard"]}); toast.success("Transaksi dihapus"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menghapus"),
  });

  const openNew = (type) => { setEditing({ transaction_number:`KAS/${new Date().getFullYear()}/${String(data.length+1).padStart(3,"0")}`, date:new Date().toISOString().slice(0,10), type, category: CASH_CATS[0], description:"", amount:0, pic:"", notes:"" }); setOpen(true); };
  const openEdit = (row) => { setEditing({ ...row }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="uang-kas-page">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#00A651]">JKTOF · Manajemen Kas</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Uang Kas JKTOF</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ["Saldo Kas", rupiah(balance), balance >= 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700", Wallet],
          ["Total Pemasukan", rupiah(totalIn), "bg-emerald-100 text-emerald-700", ArrowUp],
          ["Total Pengeluaran", rupiah(totalOut), "bg-red-100 text-red-700", ArrowDown],
          ["Total Transaksi", data.length, "bg-slate-100 text-slate-700", Package],
        ].map(([l,v,t,I]) => (
          <Card key={l} className="border-slate-200"><CardContent className="p-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 ${t}`}><I className="w-5 h-5"/></div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{l}</div>
            <div className={`text-xl font-bold ${l==="Saldo Kas" && balance < 0 ? "text-red-600" : "text-slate-900"}`} style={{fontFamily:"Outfit"}}>{v}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Cash Flow Bulanan</CardTitle></CardHeader>
        <CardContent><div className="h-56"><ResponsiveContainer><BarChart data={monthly}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/><XAxis dataKey="month" fontSize={11}/><YAxis fontSize={11} tickFormatter={(v)=>v>=1e6?`${(v/1e6).toFixed(1)}Jt`:`${v/1e3}k`}/><Tooltip formatter={(v)=>rupiah(v)}/><Legend/>
          <Bar dataKey="income" name="Pemasukan" fill="#00A651" radius={[6,6,0,0]}/>
          <Bar dataKey="expense" name="Pengeluaran" fill="#EF4444" radius={[6,6,0,0]}/>
        </BarChart></ResponsiveContainer></div></CardContent>
      </Card>

      <Card className="border-slate-200"><CardContent className="p-4 space-y-4">
        <div className="flex flex-col lg:flex-row gap-2 lg:items-center lg:justify-between">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 flex-1">
            <Input placeholder="Cari deskripsi/no. transaksi..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="kas-search"/>
            <Select value={ty} onValueChange={setTy}><SelectTrigger><SelectValue placeholder="Tipe"/></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Tipe</SelectItem><SelectItem value="Pemasukan">Pemasukan</SelectItem><SelectItem value="Pengeluaran">Pengeluaran</SelectItem></SelectContent></Select>
            <Select value={cat} onValueChange={setCat}><SelectTrigger><SelectValue placeholder="Kategori"/></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Kategori</SelectItem>{CASH_CATS.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <Select value={month} onValueChange={setMonth}><SelectTrigger><SelectValue placeholder="Bulan"/></SelectTrigger>
              <SelectContent><SelectItem value="ALL">Semua Bulan</SelectItem>{months.map(m=><SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          {canEdit && <div className="flex gap-2">
            <Button onClick={()=>openNew("Pemasukan")} className="bg-[#00A651] hover:bg-[#008741]" data-testid="kas-add-income"><ArrowUp className="w-4 h-4 mr-1"/> Pemasukan</Button>
            <Button onClick={()=>openNew("Pengeluaran")} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="kas-add-expense"><ArrowDown className="w-4 h-4 mr-1"/> Pengeluaran</Button>
          </div>}
        </div>
        <div className="rounded-lg border border-slate-200 overflow-hidden overflow-x-auto">
          <Table className="fodm-table"><TableHeader className="bg-slate-50"><TableRow>
            <TableHead>Tanggal</TableHead><TableHead>No.</TableHead><TableHead>Tipe</TableHead><TableHead>Kategori</TableHead><TableHead>Deskripsi</TableHead><TableHead className="text-right">Jumlah</TableHead><TableHead>PIC</TableHead><TableHead className="text-right">Saldo</TableHead>{canEdit && <TableHead className="text-right">Aksi</TableHead>}
          </TableRow></TableHeader><TableBody>
            {withBalance.length === 0 ? <TableRow><TableCell colSpan={canEdit ? 9 : 8} className="text-center py-8 italic text-slate-500">Tidak ada transaksi.</TableCell></TableRow>
            : withBalance.map(t => (
              <TableRow key={t.id} data-testid="kas-row">
                <TableCell className="text-xs">{t.date}</TableCell>
                <TableCell className="mono text-xs">{t.transaction_number}</TableCell>
                <TableCell>{t.type === "Pemasukan" ? <Badge className="bg-emerald-100 text-emerald-700">MASUK</Badge> : <Badge className="bg-red-100 text-red-700">KELUAR</Badge>}</TableCell>
                <TableCell><Badge variant="outline">{t.category}</Badge></TableCell>
                <TableCell className="max-w-xs truncate text-sm">{t.description}</TableCell>
                <TableCell className={`text-right font-semibold ${t.type==="Pemasukan"?"text-emerald-700":"text-red-600"}`}>{t.type==="Pemasukan"?"+":"-"}{rupiah(t.amount)}</TableCell>
                <TableCell className="text-xs">{t.pic}</TableCell>
                <TableCell className={`text-right mono text-xs font-semibold ${t.running < 0 ? "text-red-600":"text-slate-800"}`}>{rupiah(t.running)}</TableCell>
                {canEdit && <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={()=>openEdit(t)} data-testid="kas-edit"><Pencil className="w-4 h-4"/></Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600" data-testid="kas-delete"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Hapus transaksi?</AlertDialogTitle><AlertDialogDescription>Transaksi <b>{t.transaction_number}</b> ({rupiah(t.amount)}) akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(t.id)} data-testid="kas-delete-confirm">Hapus</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>}
              </TableRow>
            ))}
          </TableBody></Table>
        </div>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah Transaksi" : (editing?.type === "Pemasukan" ? "Tambah Pemasukan" : "Tambah Pengeluaran")}</DialogTitle></DialogHeader>
          {editing && <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>No. Transaksi</Label><Input value={editing.transaction_number} onChange={(e)=>setEditing({...editing, transaction_number:e.target.value})} data-testid="kas-form-no"/></div>
            <div><Label>Tanggal</Label><Input type="date" value={editing.date} onChange={(e)=>setEditing({...editing, date:e.target.value})} data-testid="kas-form-date"/></div>
            <div><Label>Tipe</Label>
              <Select value={editing.type} onValueChange={(v)=>setEditing({...editing, type:v})}><SelectTrigger data-testid="kas-form-type"><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="Pemasukan">Pemasukan</SelectItem><SelectItem value="Pengeluaran">Pengeluaran</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Kategori</Label>
              <Select value={editing.category} onValueChange={(v)=>setEditing({...editing, category:v})}><SelectTrigger data-testid="kas-form-cat"><SelectValue/></SelectTrigger>
              <SelectContent>{CASH_CATS.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Jumlah (Rp)</Label><Input type="number" value={editing.amount} onChange={(e)=>setEditing({...editing, amount:Number(e.target.value)})} data-testid="kas-form-amount"/></div>
            <div className="col-span-2"><Label>Deskripsi</Label><Input value={editing.description} onChange={(e)=>setEditing({...editing, description:e.target.value})} data-testid="kas-form-desc"/></div>
            <div className="col-span-2"><Label>PIC</Label><Input value={editing.pic} onChange={(e)=>setEditing({...editing, pic:e.target.value})} data-testid="kas-form-pic"/></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button><Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)} data-testid="kas-form-save">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- JKTOF Overview Dashboard ----------
export function JKTOFDashboardPage() {
  const { isAdmin } = useAuth();
  const { data } = useQuery({ queryKey:["jktof-dashboard"], queryFn: async () => (await apiClient.get("/jktof/dashboard")).data });
  const { data: logs = [] } = useQuery({ queryKey:["jktof-logs"], queryFn: async () => (await apiClient.get("/activity-logs", { params:{ limit:30 } })).data, enabled: isAdmin() });
  const jktofLogs = logs.filter(l => l.module?.includes("OF") || l.module?.includes("Kas") || l.module?.includes("Inventaris"));

  const of = data?.of || {}; const ofc = data?.ofc || {}; const cash = data?.cash || {};

  return (
    <div className="space-y-6" data-testid="jktof-dashboard">
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-widest text-[#FFC72C]">JKTOF Overview</div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Dashboard JKTOF</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan inventaris OF & OFC dan status kas divisi.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building className="w-4 h-4 text-[#00A651]"/> Inventaris OF</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-4 gap-3 text-center">
            {[["Item",of.total_items],["Tersedia",of.available],["Dipakai",of.in_use],["Rusak",of.damaged]].map(([l,v]) => (
              <div key={l}><div className="text-2xl font-bold text-slate-800" style={{fontFamily:"Outfit"}}>{v ?? "—"}</div><div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">{l}</div></div>
            ))}
          </div></CardContent>
        </Card>
        <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Building className="w-4 h-4 text-[#FFC72C]"/> Inventaris OFC</CardTitle></CardHeader>
          <CardContent><div className="grid grid-cols-4 gap-3 text-center">
            {[["Item",ofc.total_items],["Tersedia",ofc.available],["Dipakai",ofc.in_use],["Rusak",ofc.damaged]].map(([l,v]) => (
              <div key={l}><div className="text-2xl font-bold text-slate-800" style={{fontFamily:"Outfit"}}>{v ?? "—"}</div><div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">{l}</div></div>
            ))}
          </div></CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-1"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wallet className="w-4 h-4 text-[#00A651]"/> Uang Kas</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><div className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">Saldo Saat Ini</div>
              <div className={`text-2xl font-bold ${(cash.balance||0)<0?"text-red-600":"text-slate-800"}`} style={{fontFamily:"Outfit"}}>{rupiah(cash.balance)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="p-2 rounded-lg bg-emerald-50"><div className="text-[10px] font-semibold text-emerald-700">PEMASUKAN</div><div className="text-sm font-bold text-emerald-800 mono">{rupiah(cash.income)}</div></div>
              <div className="p-2 rounded-lg bg-red-50"><div className="text-[10px] font-semibold text-red-700">PENGELUARAN</div><div className="text-sm font-bold text-red-800 mono">{rupiah(cash.expense)}</div></div>
            </div>
            <div className="text-[10px] text-slate-500">{cash.transactions || 0} transaksi tercatat</div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 lg:col-span-2"><CardHeader className="pb-2"><CardTitle className="text-sm">Cash Flow Bulanan</CardTitle></CardHeader>
          <CardContent><div className="h-52"><ResponsiveContainer><BarChart data={cash.monthly || []}>
            <XAxis dataKey="month" fontSize={11}/><YAxis fontSize={11} tickFormatter={(v)=>v>=1e6?`${(v/1e6).toFixed(1)}Jt`:`${v/1e3}k`}/><Tooltip formatter={(v)=>rupiah(v)}/><Legend/>
            <Bar dataKey="income" name="Pemasukan" fill="#00A651" radius={[6,6,0,0]}/>
            <Bar dataKey="expense" name="Pengeluaran" fill="#EF4444" radius={[6,6,0,0]}/>
          </BarChart></ResponsiveContainer></div></CardContent>
        </Card>
      </div>

      <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Aktivitas JKTOF Terkini</CardTitle></CardHeader>
        <CardContent className="space-y-2 max-h-72 overflow-y-auto">
          {jktofLogs.length === 0 ? <div className="text-xs italic text-slate-500">Belum ada aktivitas.</div>
          : jktofLogs.slice(0,20).map(l => (
            <div key={l.id} className="flex gap-3 text-xs p-2 rounded hover:bg-slate-50">
              <div className="w-1.5 h-1.5 rounded-full bg-[#FFC72C] mt-1.5"/>
              <div className="flex-1"><div className="text-slate-800"><span className="font-semibold">{l.user_name}</span> · {l.description}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{l.module} · {new Date(l.timestamp).toLocaleString("id-ID")}</div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
